import { API_URL } from '../constants';
import { apiFetch } from './client';

/**
 * GET /admin-api/aav/print-files/jobs
 *
 * Returns one page of line items that have a print file, together with the
 * print file record and the full (non-paged) job history for that print file.
 *
 *  - page: integer from 1 (default 1)
 *  - limit: 1–100 (default 20)
 *  - sortDirection: ASC | DESC (default DESC) — sorts by line item id
 *
 * CDN URLs are unsigned public URLs (https://s3.aav-services.com/{key}) and are
 * null until the corresponding file exists.
 */

export type PrintFileColor = 'black' | 'white';
export type PrintFileStatus = 'created' | 'pending_design_review' | 'in_review' | 'reviewed';
export type PrintFileReviewResult = 'ok' | 'need_design' | 'need_cs';
export type PrintFileJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped';

export interface IPrintFile {
    id: string;
    orderId: string;
    sku: string;
    color: PrintFileColor | string;
    croppingNotes: string | null;
    printFileStatus: PrintFileStatus | string;
    reviewResult: PrintFileReviewResult | string | null;
    hasManualEdits: boolean | null;
    draftPrintReadyUrl: string | null;
    finalPrintReadyUrl: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface IPrintFileJob {
    id: string;
    status: PrintFileJobStatus | string;
    /** Exactly one job per print file is current when a current job exists. */
    current: boolean;
    preprocessedPrintReadyUrl: string | null;
    backgroundRemovedUrl: string | null;
    errorStep: string | null;
    errorType: string | null;
    errorMessage: string | null;
    skipReason: string | null;
    createdAt: string;
    finishedAt: string | null;
}

export interface IPrintFileLineItem {
    lineItemId: string;
    printReadyFileId: string;
    /** Customer image URL from Shopify. */
    originalUrl: string | null;
    /** Mockup URL from Shopify, or null. */
    mockupUrl: string | null;
    /** finalPrintReadyUrl, else draftPrintReadyUrl, else current job preprocessedPrintReadyUrl. */
    printFileUrl: string | null;
    printFile: IPrintFile;
    /** Full history, newest first. Line items sharing a print file share this list. */
    jobs: IPrintFileJob[];
}

export interface IPrintFileJobsResponse {
    result: IPrintFileLineItem[];
    totalCount: number;
    page: number;
    perPage: number;
}

export interface IPrintFileJobsQueryParams {
    page?: number;
    limit?: number;
    sortDirection?: 'ASC' | 'DESC';
    /** Filled from the response; not sent to the server. */
    total?: number;
}

export const PRINT_FILE_JOBS_MAX_LIMIT = 100;
export const PRINT_FILE_JOBS_LIMIT_OPTIONS = [10, 20, 50, 100];

const getAuthHeaders = () => {
    return {
        'Content-Type': 'application/json',
    };
};

const handleResponse = async (res: Response) => {
    if (!res.ok) {
        let message = res.status === 400 ? 'Invalid query parameters' : 'Server Error!';
        try {
            const body = await res.json();
            if (body?.message) message = body.message;
        } catch (e) {
            /* ignore */
        }
        throw new Error(message);
    }
    return await res.json();
};

export const stringifyPrintFileJobsParameters = (params: IPrintFileJobsQueryParams): string => {
    const query: string[] = [];
    try {
        if (params.page) query.push(`page=${Math.max(1, Math.floor(params.page))}`);
        if (params.limit) {
            const limit = Math.min(PRINT_FILE_JOBS_MAX_LIMIT, Math.max(1, Math.floor(params.limit)));
            query.push(`limit=${limit}`);
        }
        if (params.sortDirection === 'ASC' || params.sortDirection === 'DESC') {
            query.push(`sortDirection=${params.sortDirection}`);
        }
    } catch (e) {
        console.warn('Error building print file jobs query params');
    }
    return query.join('&');
};

export const getPrintFileJobs = async (
    queryParams: IPrintFileJobsQueryParams
): Promise<IPrintFileJobsResponse> => {
    const res = await apiFetch(
        `${API_URL}/aav/print-files/jobs?${stringifyPrintFileJobsParameters(queryParams)}`,
        { method: 'GET', headers: getAuthHeaders() }
    );
    const data = await handleResponse(res);
    return {
        result: Array.isArray(data?.result) ? data.result : [],
        totalCount: Number(data?.totalCount ?? 0),
        page: Number(data?.page ?? queryParams.page ?? 1),
        perPage: Number(data?.perPage ?? queryParams.limit ?? 20),
    };
};

/** The job flagged `current`, falling back to the newest job when none is flagged. */
export const getCurrentJob = (item: IPrintFileLineItem): IPrintFileJob | null => {
    if (!item.jobs?.length) return null;
    return item.jobs.find((j) => j.current) ?? item.jobs[0] ?? null;
};

/** Which field the server resolved `printFileUrl` from. */
export const getPrintFileUrlSource = (
    item: IPrintFileLineItem
): 'final' | 'draft' | 'preprocessed' | null => {
    if (!item.printFileUrl) return null;
    if (item.printFile?.finalPrintReadyUrl === item.printFileUrl) return 'final';
    if (item.printFile?.draftPrintReadyUrl === item.printFileUrl) return 'draft';
    return 'preprocessed';
};
